import { create } from 'zustand';
import { startOfMonth, endOfMonth, subMonths } from 'date-fns';
import { useEffect, useRef, useCallback } from 'react';
import { useUrlValue } from './urlStateStore';
import type { PostStatus } from '../components/common/PostStatusDropdown';
import type { MediaFormat } from '../components/common/MediaFormatDropdown';
import type { PostFormat } from '../components/common/PostFormatDropdown';
import { useSearchParams } from 'react-router-dom';

// Calendar page state
interface CalendarState {
  dateRange: 'this-month' | 'last-month' | 'this-year' | number | 'custom';
  customDateRange: {
    start: Date;
    end: Date;
  };
  selectedBrand: string;
  view: 'calendar' | 'grid' | 'board' | 'list';
  statusFilter: PostStatus;
  mediaFormat: MediaFormat;
  postFormat: PostFormat;
  currentDate: Date;
  
  // Actions
  setDateRange: (dateRange: 'this-month' | 'last-month' | 'this-year' | number | 'custom') => void;
  setCustomDateRange: (range: { start: Date; end: Date }) => void;
  setSelectedBrand: (brandId: string) => void;
  setView: (view: 'calendar' | 'grid' | 'board' | 'list') => void;
  setStatusFilter: (status: PostStatus) => void;
  setMediaFormat: (format: MediaFormat) => void;
  setPostFormat: (format: PostFormat) => void;
  setCurrentDate: (date: Date) => void;
}

// Create the store with default values
export const useCalendarStore = create<CalendarState>((set, get) => {
  const lastMonth = subMonths(new Date(), 1);
  
  return {
    dateRange: 'this-month',
    customDateRange: {
      start: startOfMonth(lastMonth),
      end: endOfMonth(lastMonth)
    },
    selectedBrand: 'all',
    view: 'calendar',
    statusFilter: 'all',
    mediaFormat: 'all',
    postFormat: 'all',
    currentDate: new Date(),
    
    // Actions
    setDateRange: (dateRange) => set((state) => {
      if (state.dateRange === dateRange) return state;
      return { dateRange };
    }),
    setCustomDateRange: (customDateRange) => set({ customDateRange }),
    setSelectedBrand: (selectedBrand) => set((state) => {
      if (state.selectedBrand === selectedBrand) return state;
      return { selectedBrand };
    }),
    setView: (view) => set((state) => {
      if (state.view === view) return state;
      return { view };
    }),
    setStatusFilter: (statusFilter) => set((state) => {
      if (state.statusFilter === statusFilter) return state;
      return { statusFilter };
    }),
    setMediaFormat: (mediaFormat) => set((state) => {
      if (state.mediaFormat === mediaFormat) return state;
      return { mediaFormat };
    }),
    setPostFormat: (postFormat) => set((state) => {
      if (state.postFormat === postFormat) return state;
      return { postFormat };
    }),
    setCurrentDate: (currentDate) => set({ currentDate })
  };
});

// Hook to use calendar state with URL synchronization
export function useCalendarUrlState() {
  // Get the raw state and actions from Zustand
  const store = useCalendarStore();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Create refs to track initial values and prevent circular updates
  const isInitialMount = useRef(true);
  const updatingFromStore = useRef(false);
  const storeRef = useRef(store);
  
  // Update store ref
  useEffect(() => {
    storeRef.current = store;
  }, [store]);
  
  // Clean up URL by removing unwanted parameters
  useEffect(() => {
    let shouldUpdateParams = false;
    const newParams = new URLSearchParams(searchParams);
    
    // Remove 'platform' parameter if it exists (belongs to Analytics page)
    if (newParams.has('platform')) {
      newParams.delete('platform');
      shouldUpdateParams = true;
    }
    
    // Remove 'tab' parameter if it exists (not used in Calendar)
    if (newParams.has('tab')) {
      newParams.delete('tab');
      shouldUpdateParams = true;
    }
    
    if (shouldUpdateParams) {
      setSearchParams(newParams);
    }
  }, [searchParams, setSearchParams]);
  
  // Connect state values to URL parameters with stable options
  const viewOptions = useRef({ skipSync: false }).current;
  const statusOptions = useRef({ skipSync: false }).current;
  const brandOptions = useRef({ skipSync: false }).current;
  const dateRangeOptions = useRef({ skipSync: false }).current;
  const mediaFormatOptions = useRef({ skipSync: false }).current;
  const postFormatOptions = useRef({ skipSync: false }).current;
  
  // Connect state values to URL parameters
  const [view, setView] = useUrlValue<'calendar' | 'grid' | 'board' | 'list'>(
    'view', 
    store.view,
    viewOptions
  );
  
  const [statusFilter, setStatusFilter] = useUrlValue<PostStatus>(
    'status',
    store.statusFilter,
    statusOptions
  );
  
  const [selectedBrand, setSelectedBrand] = useUrlValue<string>(
    'brand',
    store.selectedBrand,
    brandOptions
  );
  
  const [dateRange, setDateRange] = useUrlValue<'this-month' | 'last-month' | 'this-year' | number | 'custom'>(
    'dateRange',
    store.dateRange,
    dateRangeOptions
  );
  
  const [mediaFormat, setMediaFormat] = useUrlValue<MediaFormat>(
    'mediaFormat',
    store.mediaFormat,
    mediaFormatOptions
  );
  
  const [postFormat, setPostFormat] = useUrlValue<PostFormat>(
    'postFormat',
    store.postFormat,
    postFormatOptions
  );
  
  // On initial mount, sync the store with URL values (URL has priority)
  useEffect(() => {
    if (isInitialMount.current) {
      // Only update the store if URL values are different from defaults
      const currentStore = storeRef.current;
      
      if (view !== currentStore.view) currentStore.setView(view);
      if (statusFilter !== currentStore.statusFilter) currentStore.setStatusFilter(statusFilter);
      if (selectedBrand !== currentStore.selectedBrand) currentStore.setSelectedBrand(selectedBrand);
      if (dateRange !== currentStore.dateRange) currentStore.setDateRange(dateRange);
      
      isInitialMount.current = false;
    }
  }, []); // Empty dependency array ensures this runs only once
  
  // Create wrapper functions for setters that update both URL and store
  const setViewWrapper = useCallback((newView: 'calendar' | 'grid' | 'board' | 'list') => {
    // Skip if the value hasn't changed
    if (newView === view) return;
    
    // Update URL first
    setView(newView);
    
    // Then update store, but mark that we're updating from a user action
    updatingFromStore.current = true;
    storeRef.current.setView(newView);
    updatingFromStore.current = false;
  }, [view, setView]);
  
  const setStatusFilterWrapper = useCallback((newStatus: PostStatus) => {
    if (newStatus === statusFilter) return;
    
    setStatusFilter(newStatus);
    updatingFromStore.current = true;
    storeRef.current.setStatusFilter(newStatus);
    updatingFromStore.current = false;
  }, [statusFilter, setStatusFilter]);
  
  const setSelectedBrandWrapper = useCallback((newBrand: string) => {
    if (newBrand === selectedBrand) return;
    
    setSelectedBrand(newBrand);
    updatingFromStore.current = true;
    storeRef.current.setSelectedBrand(newBrand);
    updatingFromStore.current = false;
  }, [selectedBrand, setSelectedBrand]);
  
  const setDateRangeWrapper = useCallback((newDateRange: 'this-month' | 'last-month' | 'this-year' | number | 'custom') => {
    if (newDateRange === dateRange) return;
    
    setDateRange(newDateRange);
    updatingFromStore.current = true;
    storeRef.current.setDateRange(newDateRange);
    updatingFromStore.current = false;
  }, [dateRange, setDateRange]);
  
  // Keep Zustand store in sync with URL changes, but only if it's coming from the URL
  // and not from our wrapper functions
  useEffect(() => {
    if (!isInitialMount.current && !updatingFromStore.current && view !== storeRef.current.view) {
      storeRef.current.setView(view);
    }
  }, [view]);
  
  useEffect(() => {
    if (!isInitialMount.current && !updatingFromStore.current && statusFilter !== storeRef.current.statusFilter) {
      storeRef.current.setStatusFilter(statusFilter);
    }
  }, [statusFilter]);
  
  useEffect(() => {
    if (!isInitialMount.current && !updatingFromStore.current && selectedBrand !== storeRef.current.selectedBrand) {
      storeRef.current.setSelectedBrand(selectedBrand);
    }
  }, [selectedBrand]);
  
  useEffect(() => {
    if (!isInitialMount.current && !updatingFromStore.current && dateRange !== storeRef.current.dateRange) {
      storeRef.current.setDateRange(dateRange);
    }
  }, [dateRange]);
  
  // Return URL-synced values and our wrapped setters
  return {
    // URL synced values
    view,
    statusFilter,
    selectedBrand,
    dateRange,
    
    // URL synced setters (using our wrapper functions)
    setView: setViewWrapper,
    setStatusFilter: setStatusFilterWrapper,
    setSelectedBrand: setSelectedBrandWrapper,
    setDateRange: setDateRangeWrapper,
    
    // Values not synced with URL
    customDateRange: store.customDateRange,
    currentDate: store.currentDate,
    
    // Setters not synced with URL
    setCustomDateRange: store.setCustomDateRange,
    setCurrentDate: store.setCurrentDate
  };
} 